import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import {
  format, addDays, startOfWeek, endOfWeek, addWeeks, subWeeks,
  startOfMonth, endOfMonth, addMonths, subMonths,
} from 'date-fns';

import { CalendarToolbar } from '@/components/calendar/CalendarToolbar';
import { CalendarFilters } from '@/components/calendar/CalendarFilters';
import { CalendarWeekView } from '@/components/calendar/CalendarWeekView';
import { CalendarDayView } from '@/components/calendar/CalendarDayView';
import { CalendarMonthView } from '@/components/calendar/CalendarMonthView';
import { CalendarTimelineView } from '@/components/calendar/CalendarTimelineView';
import { CalendarShiftDrawer } from '@/components/calendar/CalendarShiftDrawer';
import { CalendarSILHouseDrawer } from '@/components/calendar/CalendarSILHouseDrawer';
import { CalendarCentreDrawer } from '@/components/calendar/CalendarCentreDrawer';
import { CalendarPublishDialog } from '@/components/calendar/CalendarPublishDialog';
import { CalendarCreateShiftModal } from '@/components/calendar/CalendarCreateShiftModal';
import { getShiftPeriod } from '@/components/calendar/ShiftPeriodBadge';

// CalendarV3 — Scheduling Board.
// State orchestration only. UI broken into /components/calendar/* (see docs/tech-debt.md).
export default function CalendarV3() {
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const [shifts, setShifts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [services, setServices] = useState([]);
  const [, setHouses] = useState([]);
  const [, setParticipantHouseMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // day | week | month | timeline
  const [groupBy, setGroupBy] = useState('participant'); // participant | staff | sil | centre
  const [statusFilter, setStatusFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState([]); // multi-select shift periods
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedShift, setSelectedShift] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createPrefill, setCreatePrefill] = useState({ date: '', staffId: '' });
  const [silDrawer, setSilDrawer] = useState({ open: false, houseId: null });
  const [centreDrawer, setCentreDrawer] = useState({ open: false, sessionId: null });
  const [publishOpen, setPublishOpen] = useState(false);

  // Compute date range based on view
  const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const dateRange = useMemo(() => {
    if (viewMode === 'day') return { start: format(currentDate, 'yyyy-MM-dd'), end: format(currentDate, 'yyyy-MM-dd') };
    if (viewMode === 'month') {
      const ms = startOfMonth(currentDate);
      const me = endOfMonth(currentDate);
      return { start: format(startOfWeek(ms, { weekStartsOn: 1 }), 'yyyy-MM-dd'), end: format(endOfWeek(me, { weekStartsOn: 1 }), 'yyyy-MM-dd') };
    }
    return { start: format(days[0], 'yyyy-MM-dd'), end: format(days[6], 'yyyy-MM-dd') };
  }, [viewMode, currentDate, days]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [shR, stR, pR, svR, hR] = await Promise.all([
        api.get('/roster/shifts', { params: { start_date: dateRange.start, end_date: dateRange.end, limit: 500 } }),
        api.get('/staff', { params: { limit: 200 } }),
        api.get('/participants', { params: { limit: 200 } }),
        api.get('/services/catalog', { params: { limit: 200 } }).catch(() => ({ data: { items: [], data: [] } })),
        api.get('/houses', { params: { limit: 100 } }).catch(() => ({ data: { items: [], data: [] } })),
      ]);
      const shiftsData = shR.data.items || shR.data.data || [];
      const housesData = hR.data.items || hR.data.data || [];

      // Build participant → house map from active accommodation allocations
      const phMap = {};
      const houseNameMap = {};
      housesData.forEach(h => { houseNameMap[h.id] = h.name || h.house_no; });

      const allocResults = await Promise.all(
        housesData.map(h => api.get(`/accommodation/by-house/${h.id}`).catch(() => ({ data: [] })))
      );
      allocResults.forEach(res => {
        const allocs = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.data || []);
        allocs.forEach(a => {
          if (a.status === 'ACTIVE' && a.participant_id && a.house_id) {
            phMap[a.participant_id] = { house_id: a.house_id, house_name: houseNameMap[a.house_id] || a.house_name || `House ${a.house_id.slice(-4)}` };
          }
        });
      });
      setParticipantHouseMap(phMap);

      // Enrich shifts with house info from allocations if not already set
      const enrichedShifts = shiftsData.map(s => {
        if (!s.house_id && s.participant_id && phMap[s.participant_id]) {
          return { ...s, _sil_house_id: phMap[s.participant_id].house_id, _sil_house_name: phMap[s.participant_id].house_name };
        }
        return s;
      });

      setShifts(enrichedShifts);
      setStaff(stR.data.items || stR.data.data || []);
      setParticipants(pR.data.items || pR.data.data || []);
      setServices(svR.data.items || svR.data.data || []);
      setHouses(housesData);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Keep the open drawer's `selectedShift` in sync with the latest fetched
  // copy. Without this, after `onUpdate()` the parent refetches `shifts` but
  // the drawer's `shift` prop is still the stale snapshot taken when the
  // user clicked the card — leading to "changes only show after refresh".
  useEffect(() => {
    if (!selectedShift) return;
    const fresh = shifts.find(s => s.id === selectedShift.id);
    if (fresh && fresh !== selectedShift) {
      setSelectedShift(fresh);
    }
  }, [shifts, selectedShift]);

  // Filter
  const filteredShifts = useMemo(() => {
    let r = shifts;
    if (statusFilter !== 'all') r = r.filter(s => s.status === statusFilter);
    if (periodFilter && periodFilter.length > 0) {
      const set = new Set(periodFilter);
      r = r.filter(s => set.has(getShiftPeriod(s).code));
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      r = r.filter(s => (s.participant_name || '').toLowerCase().includes(q) || (s.service_name || '').toLowerCase().includes(q) || (s.assigned_staff || []).some(st => (st.name || '').toLowerCase().includes(q)));
    }
    return r;
  }, [shifts, statusFilter, periodFilter, searchTerm]);

  // Navigation
  const goNext = () => {
    if (viewMode === 'day') setCurrentDate(d => addDays(d, 1));
    else if (viewMode === 'month') setCurrentDate(d => addMonths(d, 1));
    else setCurrentDate(d => addWeeks(d, 1));
  };
  const goPrev = () => {
    if (viewMode === 'day') setCurrentDate(d => addDays(d, -1));
    else if (viewMode === 'month') setCurrentDate(d => subMonths(d, 1));
    else setCurrentDate(d => subWeeks(d, 1));
  };
  const goToday = () => setCurrentDate(new Date());

  const handleShiftClick = (shift) => { setSelectedShift(shift); setDrawerOpen(true); };
  const handleCellClick = (staffMember, dateStr) => {
    setCreatePrefill({ date: dateStr, staffId: staffMember?.id || '' });
    setCreateOpen(true);
  };
  const handleRowClick = (row) => {
    if (row.kind === 'sil') setSilDrawer({ open: true, houseId: row.id });
    else if (row.kind === 'centre') setCentreDrawer({ open: true, sessionId: row.id });
  };
  const handleUpdate = () => { fetchData(); };

  // Drag & drop reschedule
  const handleDragDrop = async (shiftId, newDate) => {
    try {
      await api.put(`/roster/shifts/${shiftId}`, { shift_date: newDate });
      toast.success(`Shift moved to ${newDate}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to move shift');
    }
  };

  // Date display
  const dateLabel = viewMode === 'day' ? format(currentDate, 'EEEE, d MMMM yyyy')
    : viewMode === 'month' ? format(currentDate, 'MMMM yyyy')
    : `${format(days[0], 'd MMM')} – ${format(days[6], 'd MMM yyyy')}`;

  // Publish status — counts shifts in current visible range that have NEVER been published,
  // OR were modified after their last publish (updated_at > published_at).
  const publishStats = useMemo(() => {
    let unpublished = 0;
    let lastPublishedAt = null;
    for (const s of filteredShifts) {
      if (s.status === 'CANCELLED') continue;
      const pub = s.published_at;
      const upd = s.updated_at;
      if (!pub) {
        unpublished += 1;
      } else if (upd && new Date(upd) > new Date(pub)) {
        unpublished += 1;
      }
      if (pub && (!lastPublishedAt || new Date(pub) > new Date(lastPublishedAt))) {
        lastPublishedAt = pub;
      }
    }
    return { unpublishedCount: unpublished, lastPublishedAt };
  }, [filteredShifts]);

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col" data-testid="calendar-v3">
      <CalendarToolbar
        viewMode={viewMode} setViewMode={setViewMode}
        filteredShifts={filteredShifts} dateLabel={dateLabel}
        onPrev={goPrev} onToday={goToday} onNext={goNext}
        onCreateShift={() => { setCreatePrefill({ date: format(currentDate, 'yyyy-MM-dd'), staffId: '' }); setCreateOpen(true); }}
        onPublish={() => setPublishOpen(true)}
        onToggleFilters={() => setShowFilters(!showFilters)}
        onRefresh={fetchData}
        unpublishedCount={publishStats.unpublishedCount}
        lastPublishedAt={publishStats.lastPublishedAt}
      />

      <div className="flex-1 flex overflow-hidden">
        {showFilters && (
          <CalendarFilters
            searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
            periodFilter={periodFilter} setPeriodFilter={setPeriodFilter}
            groupBy={groupBy} setGroupBy={setGroupBy}
          />
        )}

        <div className="flex-1 overflow-auto bg-white" data-testid="calendar-grid">
          {loading ? (
            <div className="flex items-center justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : viewMode === 'day' ? (
            <CalendarDayView date={currentDate} filteredShifts={filteredShifts} onShiftClick={handleShiftClick} />
          ) : viewMode === 'month' ? (
            <CalendarMonthView date={currentDate} filteredShifts={filteredShifts} onShiftClick={handleShiftClick} onCellClick={handleCellClick} />
          ) : viewMode === 'timeline' ? (
            <CalendarTimelineView days={days} filteredShifts={filteredShifts} staff={staff} onShiftClick={handleShiftClick} groupBy={groupBy} />
          ) : (
            <CalendarWeekView days={days} filteredShifts={filteredShifts} staff={staff} participants={participants} groupBy={groupBy}
              onShiftClick={handleShiftClick} onCellClick={handleCellClick}
              onDragDrop={handleDragDrop} onRowClick={handleRowClick} permissions={permissions} />
          )}
        </div>
      </div>

      <CalendarShiftDrawer shift={selectedShift} open={drawerOpen} onClose={() => setDrawerOpen(false)} staff={staff} onUpdate={handleUpdate} permissions={permissions} />

      <CalendarSILHouseDrawer
        houseId={silDrawer.houseId}
        allShifts={shifts}
        open={silDrawer.open}
        onClose={() => setSilDrawer({ open: false, houseId: null })}
        onShiftClick={(s) => { setSilDrawer({ open: false, houseId: null }); handleShiftClick(s); }}
      />

      <CalendarCentreDrawer
        sessionId={centreDrawer.sessionId}
        allShifts={shifts}
        open={centreDrawer.open}
        onClose={() => setCentreDrawer({ open: false, sessionId: null })}
        onShiftClick={(s) => { setCentreDrawer({ open: false, sessionId: null }); handleShiftClick(s); }}
      />

      <CalendarPublishDialog
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        dateRange={dateRange}
        shifts={filteredShifts}
        onPublished={handleUpdate}
      />

      <CalendarCreateShiftModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleUpdate}
        prefillDate={createPrefill.date} prefillStaffId={createPrefill.staffId}
        participants={participants} services={services} staff={staff} />
    </div>
  );
}
